import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, SearchCheck } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '@/components/shared/empty-state';
import { TableSkeleton } from '@/components/shared/loading';
import { LoadingButton } from '@/components/shared/loading-button';
import { PageHeader } from '@/components/shared/page-header';
import { SectionCard } from '@/components/shared/section-card';
import { ToneBadge } from '@/components/shared/tone-badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import { useToast } from '@/lib/toast';

type QueueRow = { name: string; counts: Record<string, number>; dlq: number; oldest_waiting_seconds: number };
type DlqJob = { id: string; reason: string; original_id: string; created_at: string };

export function Ops() {
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<string | null>(null);
  const queues = useQuery({ queryKey: ['queues'], queryFn: () => api<{ data: QueueRow[] }>('/admin/v1/ops/queues'), refetchInterval: 5000 });
  const dlq = useQuery({ queryKey: ['dlq', selected], enabled: !!selected, queryFn: () => api<{ data: DlqJob[] }>(`/admin/v1/ops/dlq/${selected}`) });
  const replay = useMutation({
    mutationFn: () => api<{ replayed: number }>(`/admin/v1/ops/dlq/${selected}/replay`, { method: 'POST', body: {} }),
    onSuccess: (r) => { toast(`Replayed ${r.replayed} job(s)`); void qc.invalidateQueries({ queryKey: ['dlq'] }); void qc.invalidateQueries({ queryKey: ['queues'] }); },
  });

  return (
    <div className="space-y-6">
      <PageHeader title="Queues & dead letters" description="Replaying is safe: senders skip anything already delivered." />
      <SectionCard title="Queues" description="Refreshes every 5 seconds." flush>
        {queues.isLoading ? <TableSkeleton rows={4} /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-5">Queue</TableHead><TableHead>Waiting</TableHead><TableHead>Active</TableHead><TableHead>Delayed</TableHead>
                <TableHead>Failed</TableHead><TableHead>Oldest waiting</TableHead><TableHead>DLQ</TableHead><TableHead className="pr-5" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {queues.data?.data.map((q) => (
                <TableRow key={q.name} data-state={selected === q.name ? 'selected' : undefined}>
                  <TableCell className="pl-5 font-mono text-xs">{q.name}</TableCell>
                  <TableCell className="tabular-nums">{q.counts.wait}</TableCell>
                  <TableCell className="tabular-nums">{q.counts.active}</TableCell>
                  <TableCell className="tabular-nums">{q.counts.delayed}</TableCell>
                  <TableCell className="tabular-nums">{q.counts.failed}</TableCell>
                  <TableCell>{q.oldest_waiting_seconds > 300 ? <ToneBadge tone="red">{q.oldest_waiting_seconds}s</ToneBadge> : `${q.oldest_waiting_seconds}s`}</TableCell>
                  <TableCell>{q.dlq > 0 ? <ToneBadge tone="red">{q.dlq}</ToneBadge> : 0}</TableCell>
                  <TableCell className="pr-5 text-right"><Button variant="outline" size="sm" onClick={() => setSelected(q.name)}><SearchCheck /> Inspect DLQ</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>
      {selected && (
        <SectionCard
          title={<>DLQ: <span className="font-mono">{selected}</span></>}
          actions={<LoadingButton size="sm" disabled={!dlq.data?.data.length} loading={replay.isPending} onClick={() => replay.mutate()}><RotateCcw /> Replay all</LoadingButton>}
          flush
        >
          {dlq.isLoading ? <TableSkeleton rows={3} /> : dlq.data?.data.length === 0 ? <EmptyState title="Dead-letter queue is empty" /> : (
            <Table>
              <TableHeader><TableRow><TableHead className="pl-5">Job</TableHead><TableHead>Reason</TableHead><TableHead className="pr-5">Dead since</TableHead></TableRow></TableHeader>
              <TableBody>
                {dlq.data?.data.map((j) => (
                  <TableRow key={j.id}>
                    <TableCell className="pl-5 font-mono text-xs">{j.original_id}</TableCell>
                    <TableCell><div className="max-w-lg truncate">{j.reason}</div></TableCell>
                    <TableCell className="pr-5">{fmtDate(j.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </SectionCard>
      )}
    </div>
  );
}
